import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../ui/button';
import { ChevronUp, ChevronDown, BookOpen, MousePointerClick } from 'lucide-react';
import { cn } from '../../lib/utils';
import { useGameStore } from '../../store/gameStore';
import { setHighlightTarget } from '../../lib/highlightTarget';
import { toast } from 'sonner';

/**
 * ABBREVIATIONS — each entry can optionally carry a `target` describing where
 * its real button lives in the app.  When a coach taps the entry on the
 * full /key page, we set sessionStorage and navigate; the target page reads
 * it back via useHighlightTarget() and pulses an amber ring around the button.
 *
 * Buckets:
 *   A. Direct buttons (always visible during scoring) — testId only
 *   B. Inside Hits/Outs dialog — needs openDialog: 'hits' | 'outs'
 *   C. Stat-only entries (no target) — definition lookup, no link
 */
const ABBREVIATIONS = [
  // Hits / Outs dialog buttons (need active game)
  { abbr: '1B',  meaning: 'Single',                target: { path: '/game-mode', testId: 'gm-single-btn',    openDialog: 'hits',  needsGame: true } },
  { abbr: '2B',  meaning: 'Double',                target: { path: '/game-mode', testId: 'gm-double-btn',    openDialog: 'hits',  needsGame: true } },
  { abbr: '3B',  meaning: 'Triple',                target: { path: '/game-mode', testId: 'gm-triple-btn',    openDialog: 'hits',  needsGame: true } },
  { abbr: 'HR',  meaning: 'Home Run',              target: { path: '/game-mode', testId: 'gm-hr-btn',        openDialog: 'hits',  needsGame: true } },
  { abbr: 'BB',  meaning: 'Walk (Base on Balls)',  target: { path: '/game-mode', testId: 'gm-ball-btn',      hint: 'Tap Ball 4 times = automatic Walk (BB).', needsGame: true } },
  { abbr: 'K',   meaning: 'Strikeout',             target: { path: '/game-mode', testId: 'gm-k-btn',         openDialog: 'outs',  needsGame: true } },
  { abbr: 'HBP', meaning: 'Hit By Pitch',          target: { path: '/game-mode', testId: 'gm-hbp-btn',       openDialog: 'hits',  needsGame: true } },
  { abbr: 'SAC', meaning: 'Sacrifice (Bunt/Fly)',  target: { path: '/game-mode', testId: 'gm-sac-btn',       openDialog: 'hits',  needsGame: true } },
  { abbr: 'FC',  meaning: "Fielder's Choice",      target: { path: '/game-mode', testId: 'gm-fc-btn',        openDialog: 'hits',  needsGame: true } },
  { abbr: 'E',   meaning: 'Error',                 target: { path: '/game-mode', testId: 'gm-error-btn',     openDialog: 'hits',  needsGame: true } },
  { abbr: 'DP',  meaning: 'Double Play' },
  { abbr: 'TP',  meaning: 'Triple Play' },
  { abbr: 'PH',  meaning: 'Pinch Hitter',          target: { path: '/game-mode', testId: 'gm-quick-sub-btn', hint: 'PH = Pinch Hitter. Tap Quick Sub on the highlighted button to swap a hitter mid at-bat.', needsGame: true } },
  { abbr: 'CR',  meaning: 'Courtesy Runner',       target: { path: '/game-mode', testId: 'gm-quick-sub-btn', hint: 'CR = Courtesy Runner (softball). Use Quick Sub to swap a runner for the pitcher or catcher.', needsGame: true } },
  { abbr: 'SB',  meaning: 'Stolen Base',           target: { path: '/game-mode', testId: 'gm-steal-btn',     needsGame: true } },
  { abbr: 'CS',  meaning: 'Caught Stealing',       target: { path: '/game-mode', testId: 'gm-cs-btn',        needsGame: true } },
  { abbr: 'BK',  meaning: 'Balk',                  target: { path: '/game-mode', testId: 'gm-wp-btn',         hint: 'BK = Balk. The Wild Pitch button opens the dialog where you can record a Balk.', needsGame: true } },
  { abbr: 'CB',  meaning: 'Catcher Balk',          target: { path: '/game-mode', testId: 'gm-rare-btn',       hint: 'CB = Catcher Balk. Open Rare Plays → Catcher Balk for Squeeze (R3 scores · batter → 1B) or Detached Equipment (all runners +1 base).', needsGame: true } },
  // Stat columns on the Stats page (no active game required — just a team)
  { abbr: 'RBI', meaning: 'Run Batted In',         target: { path: '/stats',     testId: 'stats-col-RBI',    hint: 'RBI = Run Batted In. Counted whenever a hit, walk, or sacrifice scores a runner.' } },
  { abbr: 'AVG', meaning: 'Batting Average',       target: { path: '/stats',     testId: 'stats-col-AVG',    hint: 'AVG = Hits ÷ At Bats. .300 is generally considered great.' } },
  { abbr: 'OBP', meaning: 'On-Base Percentage',    target: { path: '/stats',     testId: 'stats-col-OBP',    hint: 'OBP = (Hits + Walks + HBP) ÷ Plate Appearances. How often a player gets on base.' } },
  { abbr: 'SLG', meaning: 'Slugging Percentage',   target: { path: '/stats',     testId: 'stats-col-SLG',    hint: 'SLG = Total Bases ÷ At Bats. Measures power — extra-base hits weigh more.' } },
  { abbr: 'OPS', meaning: 'On-Base + Slugging',    target: { path: '/stats',     testId: 'stats-col-OPS',    hint: 'OPS = OBP + SLG. The single best summary of a hitter.' } },
  { abbr: 'AB',  meaning: 'At Bat',                target: { path: '/stats',     testId: 'stats-col-AB',     hint: 'AB = At Bats. Plate appearances minus walks, HBPs, sacrifices.' } },
  // Field positions — pulse the matching dot on the field diagram
  { abbr: 'P',   meaning: 'Pitcher',               target: { path: '/field',     testId: 'field-pos-P',      hint: 'Pitcher (1) — center of the infield mound.', needsGame: true } },
  { abbr: 'C',   meaning: 'Catcher',               target: { path: '/field',     testId: 'field-pos-C',      hint: 'Catcher (2) — behind home plate.', needsGame: true } },
  { abbr: 'SS',  meaning: 'Shortstop',             target: { path: '/field',     testId: 'field-pos-SS',     hint: 'Shortstop (6) — between 2nd and 3rd base.', needsGame: true } },
  { abbr: 'LF',  meaning: 'Left Field',            target: { path: '/field',     testId: 'field-pos-LF',     hint: 'Left Field (7) — outfield, third-base side.', needsGame: true } },
  { abbr: 'CF',  meaning: 'Center Field',          target: { path: '/field',     testId: 'field-pos-CF',     hint: 'Center Field (8) — straight back from the pitcher.', needsGame: true } },
  { abbr: 'RF',  meaning: 'Right Field',           target: { path: '/field',     testId: 'field-pos-RF',     hint: 'Right Field (9) — outfield, first-base side.', needsGame: true } },
];

export default function AbbreviationKey({ variant = 'bar' }) {
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();
  const currentGameId = useGameStore((s) => s.currentGameId);

  const handleAbbrClick = (entry) => {
    if (!entry.target) return;
    if (entry.target.needsGame && !currentGameId) {
      toast.info("Start a game first — tap Coach → New Game, then come back.", { duration: 5000 });
      return;
    }
    setHighlightTarget({ ...entry.target, abbr: entry.abbr });
    navigate(entry.target.path);
  };

  if (variant === 'page') {
    return (
      <div className="min-h-screen bg-stone-100 p-4 md:p-8" data-testid="abbreviation-key-page">
        <div className="max-w-2xl mx-auto">
          <h1 className="text-3xl font-extrabold text-green-900 mb-2" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>
            ABBREVIATION KEY
          </h1>
          <p className="text-stone-500 text-sm mb-2">Quick reference for baseball &amp; softball abbreviations.</p>
          <div className="flex items-center gap-1.5 text-[11px] text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2 mb-6" data-testid="abbrev-help-banner">
            <MousePointerClick size={14} className="shrink-0" />
            <span>Tap any glowing abbreviation to jump to that button in the app — it&apos;ll pulse until you press it.</span>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            {ABBREVIATIONS.map((entry) => {
              const linkable = !!entry.target;
              const Wrapper = linkable ? 'button' : 'div';
              return (
                <Wrapper
                  key={entry.abbr}
                  onClick={linkable ? () => handleAbbrClick(entry) : undefined}
                  className={cn(
                    "flex items-center gap-3 rounded-lg p-3 border shadow-sm text-left transition-all",
                    linkable
                      ? "bg-white border-amber-300 hover:border-amber-500 hover:bg-amber-50 cursor-pointer ring-1 ring-amber-200"
                      : "bg-stone-50 border-stone-200 cursor-default"
                  )}
                  data-testid={`abbrev-row-${entry.abbr}`}
                  type={linkable ? 'button' : undefined}
                >
                  <span className={cn(
                    "w-12 text-center font-extrabold text-sm",
                    linkable ? "text-amber-700" : "text-green-800"
                  )} style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>
                    {entry.abbr}
                  </span>
                  <span className="text-stone-700 text-sm flex-1">{entry.meaning}</span>
                  {linkable && (
                    <MousePointerClick size={14} className="text-amber-500 shrink-0" />
                  )}
                </Wrapper>
              );
            })}
          </div>
        </div>
      </div>
    );
  }

  // Bottom-bar variant — definitions only, no click-to-jump (avoids hijacking scoring flow)
  return (
    <div className={cn(
      "fixed bottom-0 left-0 right-0 z-30 transition-all",
      open ? "bg-white shadow-2xl border-t-2 border-green-700" : ""
    )} data-testid="abbreviation-key-bar">
      <button
        onClick={() => setOpen(!open)}
        className={cn(
          "w-full flex items-center justify-center gap-2 py-2 text-xs font-semibold transition-all",
          open ? "bg-green-900 text-white" : "bg-stone-200 text-stone-600 hover:bg-stone-300"
        )}
        data-testid="toggle-abbrev-key"
      >
        <BookOpen size={12} />
        {open ? 'CLOSE' : 'ABBREVIATION KEY'}
        {open ? <ChevronDown size={12} /> : <ChevronUp size={12} />}
      </button>
      {open && (
        <div className="max-h-48 overflow-y-auto p-3 bg-white">
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-1.5 max-w-4xl mx-auto">
            {ABBREVIATIONS.map(({ abbr, meaning }) => (
              <div key={abbr} className="flex items-center gap-2 px-2 py-1 rounded bg-stone-50">
                <span className="font-bold text-green-800 text-xs min-w-[28px]" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>
                  {abbr}
                </span>
                <span className="text-stone-600 text-[10px]">{meaning}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
